import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { NotificationService } from '../../../../pages/notifications/services/notification.service';
import { NotificationApiService } from '../../../../pages/notifications/services/notification-api.service';
import { UserNotification, notificationBody, notificationTitle } from '../../../../pages/notifications/models/notification.model';
import { ToastService } from '../../../services/toast.service';
import { DatetimeService } from '../../../services/datetime.service';

/**
 * Campana del header — centro de notificaciones in-app del usuario logueado.
 *
 * Muestra las notificaciones más recientes, badge de no leídas (alimentado por el
 * polling del NotificationService), marcado individual al hacer click (con
 * navegación a la entidad), "Marcar todas como leídas" y enlace a la página completa.
 */
@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  imports: [CommonModule, RouterModule, DropdownComponent],
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  isOpen = false;
  unreadCount = 0;
  notifications: UserNotification[] = [];
  loading = false;
  loadError = false;

  /** Cantidad de notificaciones recientes que muestra el dropdown. */
  private readonly PREVIEW_SIZE = 8;

  private unreadSub?: Subscription;

  constructor(
    private notificationState: NotificationService,
    private notificationApi: NotificationApiService,
    private datetime: DatetimeService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.unreadSub = this.notificationState.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });
  }

  ngOnDestroy(): void {
    this.unreadSub?.unsubscribe();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.loadRecent();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  private loadRecent(): void {
    this.loading = true;
    this.loadError = false;
    this.notificationApi.search({ page: 0, size: this.PREVIEW_SIZE }).subscribe({
      next: (res) => {
        this.notifications = res.content;
        this.loading = false;
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  /** Click en una notificación: marcar leída (sub-operación, sin toast) y navegar. */
  onNotificationClick(n: UserNotification): void {
    if (!n.isRead) {
      // Optimista: marcar en UI y bajar el contador de inmediato.
      n.isRead = true;
      this.notificationState.decrementUnread();
      this.notificationApi.markRead(n.id).subscribe({
        next: () => {},
        // Si falla, resincronizamos el contador real (sin toast: es sub-operación).
        error: () => this.notificationState.refreshUnreadCount(),
      });
    }

    const route = this.notificationState.resolveRoute(n);
    this.closeDropdown();
    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  /** "Marcar todas como leídas" — sí lleva toast (operación primaria del usuario). */
  markAllAsRead(): void {
    this.notificationApi.markAllRead().subscribe({
      next: (res) => {
        this.notifications.forEach(n => (n.isRead = true));
        this.notificationState.setUnreadCount(0);
        const message = res.updated === 0
          ? 'No tenías notificaciones sin leer.'
          : res.updated === 1
            ? 'Se marcó 1 notificación como leída.'
            : `Se marcaron ${res.updated} notificaciones como leídas.`;
        this.toast.success({ title: 'Notificaciones', message });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudieron marcar las notificaciones.',
        });
      },
    });
  }

  /** Tiempo relativo corto: "Ahora", "Hace 5 min", "Ayer", "26 mar 2026". */
  formatRelative(iso: string | null): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const diffMin = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `Hace ${diffHrs} h`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return this.datetime.formatDateShort(iso);
  }

  /** Título legible: nunca la llave cruda del enum (`TASK_ASSIGNED`). */
  titulo(n: UserNotification): string {
    return notificationTitle(n);
  }

  /** Cuerpo legible, o `null` si no hay nada que mostrar. */
  cuerpo(n: UserNotification): string | null {
    return notificationBody(n);
  }

  trackById(_index: number, n: UserNotification): number {
    return n.id;
  }
}
